import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.korslovo.learn');
import { OnInit, OnDestroy } from '@angular/core';
import { Service } from "src/libs/portal/season/service";
import { RU, normalizeYo } from "src/libs/portal/korslovo/i18n";
import { TTS } from "src/libs/portal/korslovo/tts";
import { gradeAnswer, GradeResult } from "src/libs/portal/korslovo/grading";

type Step = 'preview' | 'flashcard' | 'matching' | 'result';

interface WordItem {
    id: string;
    korean: string;
    pronunciation: string;
    russian: string;
    example_ko: string;
    example_ru: string;
    accepted_list?: string[];
    accepted_hint?: string;
}

@Component({
    selector: 'wiz-page-korslovo-learn',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.korslovo.learn/view.scss */
:host {
  display: flex;
  flex-direction: column;
  height: 100%;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.animate-fade-in-up {
  animation: fadeInUp 0.3s ease-out;
}

@keyframes correctPop {
  0% {
    transform: scale(0.96);
  }
  50% {
    transform: scale(1.03);
  }
  100% {
    transform: scale(1);
  }
}
.animate-correct-pop {
  animation: correctPop 0.35s ease-out;
}

@keyframes shake {
  0%, 100% {
    transform: translateX(0);
  }
  20%, 60% {
    transform: translateX(-6px);
  }
  40%, 80% {
    transform: translateX(6px);
  }
}
.animate-shake {
  animation: shake 0.35s ease-out;
}

@keyframes bounceIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  60% {
    transform: scale(1.15);
  }
  80% {
    transform: scale(0.95);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
.animate-bounce-in {
  animation: bounceIn 0.6s ease-out;
}

/* XP Badge */
.xp-earned-badge {
  animation: xpBadgeIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

@keyframes xpBadgeIn {
  0% {
    transform: scale(0) rotate(-20deg);
    opacity: 0;
  }
  60% {
    transform: scale(1.15) rotate(5deg);
    opacity: 1;
  }
  100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
}
/* Level Up Modal */
@keyframes fadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
@keyframes cardSlideUp {
  0% {
    transform: translateY(40px) scale(0.9);
    opacity: 0;
  }
  100% {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
}
@keyframes levelUpStar {
  0%, 100% {
    transform: scale(1) rotate(0deg);
  }
  25% {
    transform: scale(1.15) rotate(-10deg);
  }
  50% {
    transform: scale(1.25) rotate(0deg);
  }
  75% {
    transform: scale(1.15) rotate(10deg);
  }
}`],
})
export class PageKorslovoLearnComponent implements OnInit, OnDestroy {
    public ru = RU;
    public loading: boolean = true;
    public lessonId: string = '';
    public lesson: any = {};
    public words: WordItem[] = [];

    // Step control
    public step: Step = 'preview';
    public steps: Step[] = ['preview', 'flashcard', 'matching', 'result'];

    // Flashcard direction mode
    public fcDirectionMode: 'ko2ru' | 'ru2ko' | 'both' = 'both';
    public completedModes: string[] = [];

    // Step 2: Flashcard — Queue-based spaced interleaving
    public fcQueue: { card: WordItem; attempts: number; direction: 'ko2ru' | 'ru2ko'; acceptedStreak: number }[] = [];
    public fcMastered: Set<string> = new Set();
    public fcMistakeIds: Set<string> = new Set();
    public fcAttempts: Map<string, number> = new Map();
    public fcPhase: 'learn' | 'verify' = 'learn';
    public fcRevealed: boolean = false;
    public fcTypedAnswer: string = '';
    public fcSubmitted: boolean = false;
    public fcTypedCorrect: boolean = false;
    public fcYoAccepted: boolean = false;
    public fcDone: boolean = false;
    public fcKnown: Set<string> = new Set();
    public fcUnknown: Set<string> = new Set();

    // Accepted answers state
    public fcGradeResult: GradeResult | null = null;
    public fcPrecisionMode: boolean = false;
    public fcAcceptedCount: number = 0; // total accepted across all cards

    // Step 3: Matching quiz
    public mqQuestions: any[] = [];
    public mqIndex: number = 0;
    public mqSelected: number = -1;
    public mqAnswered: boolean = false;
    public mqCorrectCount: number = 0;

    // Step 4: Result
    public resultData: any = {};

    // XP animation state
    public xpResult: any = null;
    public showXpAnimation: boolean = false;
    public showLevelUp: boolean = false;
    public xpAnimValue: number = 0;

    // Save failure state
    public saveFailed: boolean = false;
    public saveRetrying: boolean = false;

    private tts: TTS = new TTS();

    constructor(@Inject( Service) public service: Service) { }

    public async ngOnInit() {
        await this.service.init();
        this.lessonId = WizRoute.segment.id;
        if (!this.lessonId) {
            this.service.href('/category');
            return;
        }
        await this.loadWords();
    }

    ngOnDestroy() {
        this.tts.stop();
    }

    // ─── Data loading ───
    public async loadWords() {
        this.loading = true;
        await this.service.render();

        const { code, data } = await wiz.call("words", { lesson_id: this.lessonId });
        if (code === 200) {
            this.lesson = data.lesson || {};
            this.words = data.words || [];
            // Set completed modes and auto-select next uncompleted direction
            const modesStr = data.completed_modes || '';
            this.completedModes = modesStr ? modesStr.split(',').filter((m: string) => m) : [];
            this.autoSelectDirection();
        }

        this.loading = false;
        this.step = 'preview';
        await this.service.render();
    }

    // ─── Step progress ───
    public get stepIndex(): number {
        return this.steps.indexOf(this.step);
    }

    public get totalFcCards(): number {
        return this.fcDirectionMode === 'both' ? this.words.length * 2 : this.words.length;
    }

    public get progressPercent(): number {
        if (this.step === 'preview') return 0;
        if (this.step === 'flashcard') {
            if (this.totalFcCards === 0) return 25;
            return 25 + (this.fcMastered.size / this.totalFcCards) * 25;
        }
        if (this.step === 'matching') {
            if (this.mqQuestions.length === 0) return 50;
            return 50 + (this.mqIndex / this.mqQuestions.length) * 50;
        }
        return 100;
    }

    // ─── Step 1: Preview ───
    public async setFcDirection(mode: 'ko2ru' | 'ru2ko' | 'both') {
        this.fcDirectionMode = mode;
        await this.service.render();
    }

    public isModeCompleted(mode: string): boolean {
        return this.completedModes.includes(mode);
    }

    private autoSelectDirection() {
        const allModes: ('ko2ru' | 'ru2ko' | 'both')[] = ['ko2ru', 'ru2ko', 'both'];
        const uncompleted = allModes.filter(m => !this.completedModes.includes(m));
        if (uncompleted.length > 0) {
            this.fcDirectionMode = uncompleted[0];
        } else {
            this.fcDirectionMode = 'both';
        }
    }

    public async startFlashcard() {
        let cards: { card: WordItem; attempts: number; direction: 'ko2ru' | 'ru2ko'; acceptedStreak: number }[] = [];
        if (this.fcDirectionMode === 'ko2ru' || this.fcDirectionMode === 'both') {
            cards.push(...this.words.map(c => ({ card: c, attempts: 0, direction: 'ko2ru' as const, acceptedStreak: 0 })));
        }
        if (this.fcDirectionMode === 'ru2ko' || this.fcDirectionMode === 'both') {
            cards.push(...this.words.map(c => ({ card: c, attempts: 0, direction: 'ru2ko' as const, acceptedStreak: 0 })));
        }
        this.shuffleArray(cards);
        this.fcQueue = cards;
        this.fcMastered = new Set();
        this.fcMistakeIds = new Set();
        this.fcAttempts = new Map();
        this.fcPhase = 'learn';
        this.fcRevealed = false;
        this.fcTypedAnswer = '';
        this.fcSubmitted = false;
        this.fcTypedCorrect = false;
        this.fcDone = false;
        this.fcKnown = new Set();
        this.fcUnknown = new Set();
        this.fcGradeResult = null;
        this.fcPrecisionMode = false;
        this.fcAcceptedCount = 0;
        this.step = 'flashcard';
        await this.service.render();
        this.preloadTTS();
    }

    public speak(text?: string) {
        this.tts.speak(text || this.fcCurrentCard?.korean || '');
    }

    public speakWord(word: WordItem) {
        this.tts.speak(word.korean);
    }

    // ─── Step 2: Flashcard (Queue-based) ───
    public get fcCurrentCard(): WordItem | null {
        return this.fcQueue.length > 0 ? this.fcQueue[0].card : null;
    }

    public get fcCurrentDirection(): 'ko2ru' | 'ru2ko' {
        return this.fcQueue.length > 0 ? this.fcQueue[0].direction : 'ko2ru';
    }

    public get fcCurrentAttempts(): number {
        return this.fcQueue.length > 0 ? this.fcQueue[0].attempts : 0;
    }

    public get fcProgress(): string {
        return `${this.fcMastered.size} / ${this.words.length}`;
    }

    public get fcQueueRemaining(): number {
        return this.fcQueue.length;
    }

    public async fcReveal() {
        this.fcRevealed = true;
        await this.service.render();
    }

    public async fcSubmitAnswer() {
        if (this.fcSubmitted || !this.fcCurrentCard) return;
        const typed = this.fcTypedAnswer.trim();
        const card = this.fcCurrentCard;
        const dir = this.fcCurrentDirection;

        this.fcSubmitted = true;
        this.fcYoAccepted = false;

        if (dir === 'ko2ru') {
            // 한국어→러시아어: accepted_answers 적용
            const accepted = card.accepted_list || [];
            // 정밀 모드일 때 accepted를 비움 → 대표 정답만 허용
            const effectiveAccepted = this.fcPrecisionMode ? [] : accepted;
            const result = gradeAnswer(typed, card.russian, effectiveAccepted);
            this.fcGradeResult = result;
            this.fcTypedCorrect = result.grade !== 'wrong';

            // ё/е가 포함된 exact 매치 표시
            if (result.grade === 'exact') {
                const rawInput = normalizeYo(typed.toLowerCase());
                const rawCorrect = card.russian.trim().toLowerCase();
                if (rawInput === normalizeYo(rawCorrect) && typed.toLowerCase() !== rawCorrect) {
                    this.fcYoAccepted = true;
                }
            }
        } else {
            // 러시아어→한국어: accepted_answers 미적용 (한국어는 1:1)
            const result = gradeAnswer(typed, card.korean, []);
            this.fcGradeResult = result;
            this.fcTypedCorrect = result.grade === 'exact';
        }

        this.fcRevealed = true;
        await this.service.render();

        const delay = this.fcGradeResult!.grade === 'exact' ? 800
            : this.fcGradeResult!.grade === 'accepted' ? 2000
            : 2500;

        setTimeout(async () => {
            await this.fcAdvance(this.fcGradeResult!);
        }, delay);
    }

    private async fcAdvance(result: GradeResult) {
        if (this.fcQueue.length === 0) return;
        const item = this.fcQueue.shift()!;
        const cardKey = `${item.card.id}_${item.direction}`;
        const attempts = (this.fcAttempts.get(cardKey) || 0) + 1;
        this.fcAttempts.set(cardKey, attempts);

        if (result.grade === 'exact') {
            this.fcMastered.add(cardKey);
            this.fcKnown.add(cardKey);
            this.fcUnknown.delete(cardKey);
            // Reset precision mode for this card
            item.acceptedStreak = 0;
        } else if (result.grade === 'accepted') {
            // Accepted: 큐 뒤쪽(5~8칸)에 재삽입하여 대표 정답 학습 유도
            this.fcAcceptedCount++;
            item.acceptedStreak++;
            // accepted 연속 2회 → 정밀 모드 전환 (대표 정답만 허용)
            const nextPrecision = item.acceptedStreak >= 2;
            wiz.call("add_review", { word_id: item.card.id, grade: "accepted" });
            const insertAt = Math.min(5 + Math.floor(Math.random() * 4), this.fcQueue.length);
            this.fcQueue.splice(insertAt, 0, {
                card: item.card,
                attempts: attempts,
                direction: item.direction,
                acceptedStreak: item.acceptedStreak
            });
            // 다음에 이 카드가 나올 때 정밀 모드 적용
            if (nextPrecision) {
                // Mark card for precision mode
                this.fcQueue[insertAt].acceptedStreak = item.acceptedStreak;
            }
        } else {
            // Wrong
            this.fcMistakeIds.add(cardKey);
            this.fcUnknown.add(cardKey);
            this.fcKnown.delete(cardKey);
            item.acceptedStreak = 0;
            wiz.call("add_review", { word_id: item.card.id });
            if (attempts < 3) {
                const insertAt = Math.min(3 + Math.floor(Math.random() * 3), this.fcQueue.length);
                this.fcQueue.splice(insertAt, 0, { card: item.card, attempts: attempts, direction: item.direction, acceptedStreak: 0 });
            } else {
                this.fcMastered.add(cardKey);
            }
        }

        // Reset input state
        this.fcRevealed = false;
        this.fcTypedAnswer = '';
        this.fcSubmitted = false;
        this.fcTypedCorrect = false;
        this.fcYoAccepted = false;
        this.fcGradeResult = null;

        // Check if next card is in precision mode
        if (this.fcQueue.length > 0) {
            this.fcPrecisionMode = this.fcQueue[0].acceptedStreak >= 2;
        } else {
            this.fcPrecisionMode = false;
        }

        if (this.fcQueue.length === 0) {
            if (this.fcPhase === 'learn') {
                const mistakeKeys = Array.from(this.fcMistakeIds);
                const verifyQueue: { card: WordItem; attempts: number; direction: 'ko2ru' | 'ru2ko'; acceptedStreak: number }[] = [];
                for (const key of mistakeKeys) {
                    const [wordId, dir] = key.split('_');
                    const word = this.words.find(w => w.id === wordId);
                    if (word) verifyQueue.push({ card: word, attempts: 0, direction: dir as 'ko2ru' | 'ru2ko', acceptedStreak: 0 });
                }
                if (verifyQueue.length > 0) {
                    this.fcPhase = 'verify';
                    this.shuffleArray(verifyQueue);
                    this.fcQueue = verifyQueue;
                    for (const key of mistakeKeys) {
                        this.fcMastered.delete(key);
                    }
                } else {
                    this.fcDone = true;
                }
            } else {
                this.fcDone = true;
            }
        }

        this.preloadTTS();
        await this.service.render();
    }

    public async startMatching() {
        this.buildMatchingQuestions();
        this.mqIndex = 0;
        this.mqSelected = -1;
        this.mqAnswered = false;
        this.mqCorrectCount = 0;
        this.step = 'matching';
        await this.service.render();
    }

    // ─── Step 3: Matching Quiz ───
    public get mqCurrent(): any {
        return this.mqQuestions[this.mqIndex] || null;
    }

    public get mqProgress(): string {
        return `${this.mqIndex + 1} / ${this.mqQuestions.length}`;
    }

    public async mqSelect(optionIndex: number) {
        if (this.mqAnswered) return;
        this.mqSelected = optionIndex;
        this.mqAnswered = true;
        if (optionIndex === this.mqCurrent.correctIndex) {
            this.mqCorrectCount++;
        } else {
            wiz.call("add_review", { word_id: this.mqCurrent.word.id });
        }
        await this.service.render();
    }

    public async mqNext() {
        if (this.mqIndex < this.mqQuestions.length - 1) {
            this.mqIndex++;
            this.mqSelected = -1;
            this.mqAnswered = false;
            await this.service.render();
        } else {
            await this.showResult();
        }
    }

    // ─── Step 4: Result ───
    private async showResult() {
        this.resultData = {
            totalWords: this.words.length,
            fcKnown: this.fcKnown.size,
            fcUnknown: this.fcUnknown.size,
            fcAccepted: this.fcAcceptedCount,
            mqCorrect: this.mqCorrectCount,
            mqTotal: this.mqQuestions.length,
            mqPercent: this.mqQuestions.length > 0
                ? Math.round((this.mqCorrectCount / this.mqQuestions.length) * 100)
                : 0,
        };
        this.step = 'result';
        this.saveFailed = false;
        const res = await this.saveProgress('completed');
        if (res === null) {
            this.saveFailed = true;
            await this.service.render();
            return;
        }
        if (res && res.xp) {
            this.xpResult = res.xp;
            await this.service.render();
            await this.animateXp(res.xp);
        } else {
            await this.service.render();
        }
    }

    public async retrySaveProgress() {
        this.saveRetrying = true;
        await this.service.render();
        const res = await this.saveProgress('completed');
        this.saveRetrying = false;
        if (res === null) {
            this.saveFailed = true;
            await this.service.render();
            return;
        }
        this.saveFailed = false;
        if (res && res.xp) {
            this.xpResult = res.xp;
            await this.service.render();
            await this.animateXp(res.xp);
        } else {
            await this.service.render();
        }
    }

    public get resultEmoji(): string {
        const p = this.resultData.mqPercent || 0;
        if (p >= 80) return '🎉';
        if (p >= 50) return '👍';
        return '💪';
    }

    public get resultMessage(): string {
        const p = this.resultData.mqPercent || 0;
        if (p >= 80) return this.ru.learn_result_great;
        if (p >= 50) return this.ru.learn_result_good;
        return this.ru.learn_result_retry;
    }

    public async retry() {
        this.step = 'preview';
        this.fcKnown = new Set();
        this.fcUnknown = new Set();
        this.fcAcceptedCount = 0;
        this.xpResult = null;
        this.showXpAnimation = false;
        this.showLevelUp = false;
        this.xpAnimValue = 0;
        this.saveFailed = false;
        this.saveRetrying = false;
        await this.service.render();
    }

    public goQuiz() {
        this.service.href(`/quiz/${this.lessonId}/choice`);
    }

    public goBack() {
        if (this.lesson.category_id) {
            this.service.href(`/category/${this.lesson.category_id}`);
        } else {
            this.service.href('/category');
        }
    }

    public goHome() {
        this.service.href('/dashboard');
    }

    // ─── Helpers ───
    private buildMatchingQuestions() {
        const questions: any[] = [];
        const shuffled = [...this.words];
        this.shuffleArray(shuffled);

        // Build accepted_answers lookup for conflict prevention
        const acceptedLookup = new Map<string, Set<string>>();
        for (const w of this.words) {
            const accepted = (w.accepted_list || []).map(a => a.trim().toLowerCase());
            acceptedLookup.set(w.id, new Set(accepted));
        }

        for (const word of shuffled) {
            const currentAccepted = acceptedLookup.get(word.id) || new Set();
            const currentRussian = word.russian.trim().toLowerCase();

            // Filter wrong options: exclude words whose russian is in current word's accepted_answers
            // and exclude words whose accepted_answers contain current word's russian
            const wrongOptions = this.words
                .filter(w => {
                    if (w.id === word.id) return false;
                    const wRussian = w.russian.trim().toLowerCase();
                    // Exclude if this word's russian is an accepted answer for current question
                    if (currentAccepted.has(wRussian)) return false;
                    // Exclude if current word's russian is an accepted answer for this word
                    const wAccepted = acceptedLookup.get(w.id) || new Set();
                    if (wAccepted.has(currentRussian)) return false;
                    return true;
                })
                .sort(() => Math.random() - 0.5)
                .slice(0, 3)
                .map(w => w.russian);

            const options = [...wrongOptions, word.russian];
            this.shuffleArray(options);
            const correctIndex = options.indexOf(word.russian);

            questions.push({
                word,
                options,
                correctIndex,
            });
        }
        this.mqQuestions = questions;
    }

    private shuffleArray(arr: any[]) {
        for (let i = arr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
        }
    }

    private preloadTTS() {
        const upcoming: string[] = [];
        for (let i = 0; i < Math.min(3, this.fcQueue.length); i++) {
            if (this.fcQueue[i]?.card?.korean) {
                upcoming.push(this.fcQueue[i].card.korean);
            }
        }
        if (upcoming.length > 0) {
            this.tts.preload(upcoming);
        }
    }

    private async saveProgress(status: string): Promise<any> {
        const { code, data } = await wiz.call("save_progress", {
            lesson_id: this.lessonId,
            card_index: this.words.length - 1,
            status: status,
            mode: this.fcDirectionMode
        });
        return code === 200 ? data : null;
    }

    private async animateXp(xp: any) {
        this.showXpAnimation = true;
        this.xpAnimValue = 0;
        await this.service.render();

        const target = xp.xp_earned || 0;
        const steps = 20;
        const delay = Math.max(30, 600 / steps);
        for (let i = 1; i <= steps; i++) {
            this.xpAnimValue = Math.round((target / steps) * i);
            await this.service.render();
            await new Promise(r => setTimeout(r, delay));
        }
        this.xpAnimValue = target;
        await this.service.render();

        if (xp.level_up) {
            await new Promise(r => setTimeout(r, 400));
            this.showLevelUp = true;
            await this.service.render();
        }
    }

    public async dismissLevelUp() {
        this.showLevelUp = false;
        await this.service.render();
    }
}

export default PageKorslovoLearnComponent;